/* eslint-disable @typescript-eslint/no-unused-vars */
import { Container, Sprite, Texture, Point } from 'pixi.js';
import MathUtils from '../../../../utils/MathUtils';
import { Easing } from '../../../../libs/TweenManager/easing';

export class TutorialMovementArrowView extends Container {
  private static readonly handAnimationSpeed = 0.009;

  private static readonly handCurveWidthRatio = 0.65;

  private readonly arrow = new Sprite(Texture.from('tutorial_arrow')).props({
    parent: this,
    anchor: { x: 0.27, y: 1 },
  });

  private readonly hand = new Sprite(Texture.from('tutorial_hand')).props({
    parent: this,
    anchor: { x: 0, y: 0 },
  });

  private readonly handMoveVector: Point = new Point(0, 0);

  private handMovementActive = true;

  private from!: Container;

  private to!: Container;

  private readonly fromPos: Point = new Point();

  private readonly toPos: Point = new Point();

  public animatePos = 0;

  constructor() {
    super();

    this.interactive = false;
    this.interactiveChildren = false;
  }

  public reset(): void {
    this.hand.alpha = 1;
    this.animatePos = 0;
    this.hand.removeTweens();
    this.handMovementActive = true;
    this.updateHandPosition(this.animatePos);
  }

  public pointAt(from: Container, to: Container): void {
    this.from = from;
    this.to = to;

    const { arrow, fromPos, toPos, handMoveVector } = this;

    arrow.removeTweens();

    // translate from / to positions
    arrow.position.set(fromPos.x, fromPos.y);
    this.toLocal(fromPos.set(0, 0), from, fromPos);
    this.toLocal(toPos.set(0, 0), to, toPos);

    this.position.set(fromPos.x, fromPos.y);
    arrow.rotation = 0;

    const distance = MathUtils.getDistance(fromPos.x - 10, fromPos.y, toPos.x, toPos.y);

    const rotation = MathUtils.getAngleToVector(fromPos.x, fromPos.y, toPos.x, toPos.y);
    arrow.rotation = rotation + Math.PI / 2;
    arrow.height = distance;
    arrow.scale.x = fromPos.x < toPos.x ? -1 : 1;

    arrow.tweenTo(
      { height: [distance, distance + 10, distance, distance + 10, distance] },
      3000,
      'sinInOut',
      true,
      0,
      -1,
    );

    MathUtils.getVectorFromAngle(rotation, handMoveVector, distance);
  }

  private updateHandPosition(animatePos: number): void {
    const easedPos = Easing.Quadratic.InOut(animatePos);
    const { hand, arrow, handMoveVector } = this;
    hand.x = Math.round(arrow.x + handMoveVector.x * easedPos);
    hand.y = Math.round(arrow.y + (handMoveVector.y + -35) * easedPos);

    const handScaleSign = arrow.scale.x > 0 ? 1 : -1;
    hand.x +=
      Easing.Quadratic.InOut(1 - Math.abs((animatePos - 0.5) / 0.5)) *
      (arrow.width * handScaleSign * TutorialMovementArrowView.handCurveWidthRatio);
  }

  private updateAnimatePos(deltaTime: number): number {
    const animatePos = MathUtils.clamp(
      this.animatePos + TutorialMovementArrowView.handAnimationSpeed * deltaTime,
      0,
      1,
    );

    this.animatePos = animatePos;

    if (this.animatePos === 1 && this.hand.alpha === 1) {
      void this.fadeOutAndRestart();
    }

    return animatePos;
  }

  private async fadeOutAndRestart(): Promise<void> {
    this.handMovementActive = false;
    await this.hand.tweenTo({ alpha: 0 }, 200, 'quadOut', true, 250).asyncOnComplete();
    this.animatePos = 0;
    this.updateHandPosition(this.animatePos);
    await this.hand.tweenTo({ alpha: 1 }, 200, 'quadOut', true, 250).asyncOnComplete();
    this.handMovementActive = true;
  }

  public update(deltaTime: number): void {
    if (this.handMovementActive) {
      this.updateAnimatePos(deltaTime);
      this.updateHandPosition(this.animatePos);
    }
  }

  public resize(): void {
    if (this.destroyed) return;
    if (!this.from || !this.to) return;
    this.pointAt(this.from, this.to);
  }
}
